import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import UploadRoundedIcon from "@mui/icons-material/UploadRounded";
import { resolveLandingAssetUrl } from "../../../utils/landingAssets";

export default function EditableImage({
  value,
  onUpload,
  onRemove,
  label = "Imagen",
  height = 260,
  disabled = false,
  accept = "image/svg+xml,image/jpeg,image/png,image/webp,image/avif,image/gif,image/x-icon,.svg,.ico",
  sx,
}) {
  const inputRef = React.useRef(null);
  const [editing, setEditing] = React.useState(false);
  const [selectedFile, setSelectedFile] = React.useState(null);
  const [localPreview, setLocalPreview] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const resolvedValue = resolveLandingAssetUrl(value);
  const visibleImage = localPreview || resolvedValue;

  React.useEffect(() => {
    return () => {
      if (localPreview) URL.revokeObjectURL(localPreview);
    };
  }, [localPreview]);

  const resetDraft = React.useCallback(() => {
    if (localPreview) URL.revokeObjectURL(localPreview);
    setSelectedFile(null);
    setLocalPreview("");
    setError("");
  }, [localPreview]);

  const closeEditor = (event) => {
    event?.stopPropagation?.();
    resetDraft();
    setEditing(false);
  };

  const chooseFile = (event) => {
    event.stopPropagation();
    inputRef.current?.click();
  };

  const handleFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (localPreview) URL.revokeObjectURL(localPreview);
    setSelectedFile(file);
    setLocalPreview(URL.createObjectURL(file));
    setError("");
  };

  const save = async (event) => {
    event.stopPropagation();
    if (!selectedFile) {
      setError("Seleccioná una imagen antes de guardar");
      return;
    }

    setSaving(true);
    setError("");
    try {
      await onUpload(selectedFile);
      resetDraft();
      setEditing(false);
    } catch (uploadError) {
      setError(uploadError?.message || "No se pudo subir la imagen");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (event) => {
    event.stopPropagation();
    if (!onRemove || !value) return;

    setSaving(true);
    setError("");
    try {
      await onRemove();
      resetDraft();
      setEditing(false);
    } catch (removeError) {
      setError(removeError?.message || "No se pudo quitar la imagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      onClick={(event) => {
        event.stopPropagation();
        if (!disabled && !editing) {
          resetDraft();
          setEditing(true);
        }
      }}
      sx={{
        position: "relative",
        minHeight: height,
        overflow: "hidden",
        borderRadius: 3,
        bgcolor: "rgba(25,36,47,.08)",
        backgroundImage: visibleImage ? `url(${visibleImage})` : "none",
        backgroundSize: "cover",
        backgroundPosition: "center",
        border: "1px dashed rgba(25,36,47,.26)",
        cursor: disabled ? "default" : "pointer",
        "&:hover .editable-image-hint": { opacity: 1 },
        ...sx,
      }}
    >
      {!visibleImage ? (
        <Stack alignItems="center" justifyContent="center" sx={{ height, color: "rgba(25,36,47,.48)" }}>
          <ImageRoundedIcon sx={{ fontSize: 42 }} />
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 850 }}>{label}</Typography>
        </Stack>
      ) : null}

      {!editing ? (
        <Paper
          className="editable-image-hint"
          elevation={4}
          sx={{
            position: "absolute",
            left: 12,
            bottom: 12,
            px: 1.2,
            py: 0.7,
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 900,
            opacity: value ? 0 : 1,
            transition: "opacity .15s ease",
          }}
        >
          Hacé clic para subir o reemplazar
        </Paper>
      ) : (
        <Paper
          elevation={8}
          onClick={(event) => event.stopPropagation()}
          sx={{
            position: "absolute",
            left: 12,
            right: 12,
            bottom: 12,
            p: 1,
            borderRadius: 2.5,
          }}
        >
          <Stack spacing={0.8}>
            <Stack direction="row" spacing={0.7} alignItems="center">
              <Button
                type="button"
                size="small"
                variant="outlined"
                startIcon={<UploadRoundedIcon />}
                disabled={saving}
                onClick={chooseFile}
                sx={{ flex: 1, textTransform: "none", fontWeight: 900, borderRadius: 999 }}
              >
                {selectedFile?.name || (value ? "Elegir reemplazo" : "Elegir imagen")}
              </Button>
              <Tooltip title="Guardar imagen">
                <span>
                  <IconButton size="small" disabled={saving || !selectedFile} onClick={save} sx={{ color: "#16834b" }}>
                    {saving ? <CircularProgress size={18} /> : <CheckRoundedIcon />}
                  </IconButton>
                </span>
              </Tooltip>
              {value && onRemove ? (
                <Tooltip title="Quitar imagen">
                  <span>
                    <IconButton size="small" disabled={saving} onClick={remove} sx={{ color: "#9d2d24" }}>
                      <DeleteOutlineRoundedIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
              <Tooltip title="Cancelar">
                <span>
                  <IconButton size="small" disabled={saving} onClick={closeEditor}>
                    <CloseRoundedIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
            {error ? <Alert severity="error" sx={{ py: 0 }}>{error}</Alert> : null}
          </Stack>
          <input ref={inputRef} type="file" hidden accept={accept} onChange={handleFile} />
        </Paper>
      )}
    </Box>
  );
}
